import type { CryptoType } from '@/hooks/useGetCrypto';
import useGetCrypto from '@/hooks/useGetCrypto';
import { Box, Stack, useMediaQuery, useTheme } from '@mui/material';
import Paper from '@/components/Paper';
import Typography from '@/components/Typography';
import Chip from '@/components/Chip';
import map from 'lodash/map';
import Image from 'next/image';
import useLiveCrypto from '@/hooks/useLiveCrypto';
import useResponsive from '@/hooks/useResponsive';
import numberUtils from '@/utils/numberUtils';

const source = 'otc';

const CryptoBox = ({
  data,
  pingPong
}: {
  data: CryptoType;
  pingPong: boolean;
}) => {
  const theme = useTheme();

  const handleChipColor = (change: number) => {
    if (change < 0)
      return { color: 'error' as const, labelColor: theme.palette.error.A100 };
    if (change > 0)
      return {
        color: 'success' as const,
        labelColor: theme.palette.success.A100
      };
    return {
      color: 'secondary' as const
    };
  };

  const calculateChangePrice = (change?: number) => {
    if (change) return `${change}%`;
    return '0%';
  };

  const { message, isConnected } = useLiveCrypto({
    source,
    symbol: data.symbol,
    connect: true,
    pingPong: pingPong
  });
  console.log('data similar', data);
  return (
    // <Paper elevation={14} sx={{ borderRadius: 3 }}>
    <Stack
      flexDirection="column"
      alignItems="center"
      p={3}
      width={{ xs: 157, lg: 187 }}
      sx={{ border: '3px solid #EFF2F5' }}
      gap={4}
    >
      <Stack
        flex={1}
        gap={3}
        flexDirection="row"
        justifyContent="start"
        alignItems="center"
        alignSelf="start"
      >
        <Image
          src={data.logoUrl || ''}
          width={30}
          height={30}
          alt={data.base}
        />
        <Stack gap={0.5} justifyContent="center" alignItems="start">
          <Typography
            variant="body2"
            color="textColor.A80"
            weight="normal"
            noWrap
            maxWidth={85}
          >
            {data.baseFa}
          </Typography>
          <Typography variant="body4" color="textColor.A60">
            {`${data.base}`}
          </Typography>
          <Chip
            sx={{ direction: 'rtl', height: 14, width: 48, borderRadius: 1 }}
            variant="filled"
            size="small"
            mode="light"
            // label={calculateChangePrice(Number(data[source]?.priceChange || 0))}
            label={
              <Typography variant="body4">
                {calculateChangePrice(Number(data[source]?.priceChange || 0))}
              </Typography>
            }
            {...handleChipColor(Number(data[source]?.priceChange || 0))}
          />
        </Stack>
      </Stack>
      <Stack
        alignSelf="end"
        flex={1}
        gap={0.5}
        justifyContent="center"
        alignItems="end"
        width="100%"
        py={{ xs: 1, lg: 1 }}
        px={{ xs: 3, lg: 3 }}
        sx={{ backgroundColor: theme.palette.grayBackground.A10 }}
      >
        <Stack gap={1} alignItems="end" flexDirection="row-reverse">
          <Typography variant="body2" color="textColor.A80" weight="bold">
            {isConnected && !!message?.midPrice
              ? numberUtils.formatNumber(message?.midPrice || 0)
              : numberUtils.formatNumber(data[source]?.midPrice || 0)}
          </Typography>
          <Typography variant="body3" color="textColor.A60" weight="normal">
            TMN
          </Typography>
        </Stack>
      </Stack>
    </Stack>
  );
};

const SimilarCurrencies = ({ symbols }: { symbols: string[] }) => {
  const { matches } = useResponsive();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { result } = useGetCrypto(
    { get: { side: source, filter: symbols } },
    'getSidebarList',
    symbols
  );
  const topFourResults = Array.isArray(result) ? result.slice(0, 4) : [];
  return (
    <Paper
      elevation={0}
      sx={{ paddingY: 10, width: matches({ xs: '100vw', lg: 790 }) }}
    >
      <Stack
        flexDirection="row"
        alignItems="center"
        justifyContent={matches({ xs: 'center', lg: 'space-between' })}
      >
        <Typography
          weight="bold"
          textAlign="start"
          variant="title"
          color="#142447"
          component="h4"
        >
          خرید ارزهای مشابه
        </Typography>
        {!isMobile && (
          <a
            href="/price"
            style={{
              textDecoration: 'none',
              display: matches({ xs: 'none', lg: 'block' })
            }}
          >
            <Stack flexDirection="row" alignItems="center">
              <Typography
                flex={1}
                weight="bold"
                textAlign="start"
                variant="body2"
                color="#222531"
              >
                مشاهده ارزهای بیشتر
              </Typography>
              <Image
                src="/assets/Chevron-Left.svg"
                width={20}
                height={20}
                alt=""
              />
            </Stack>
          </a>
        )}
      </Stack>
      <Stack
        flexDirection="row"
        gap={matches({ xs: 3.5, lg: 4 })}
        py={{ xs: 2, lg: 6 }}
        flexWrap={matches({ xs: 'wrap', lg: 'no-wrap' })}
        justifyContent={matches({ xs: 'center', lg: 'flex-start' })}
      >
        {map(topFourResults || [], (row: CryptoType, index) => (
          <Box
            key={row.id}
            width={{ xs: 157, lg: 187 }}
            mb={matches({ xs: 2, lg: 0 })}
          >
            <a
              href={`/price/${row.base.toLowerCase()}`}
              style={{ textDecoration: 'none' }}
            >
              <CryptoBox data={row} pingPong={index === 0} />
            </a>
          </Box>
        ))}
      </Stack>
      {isMobile && (
        <Stack pt={3}>
          <a href="/price" style={{ textDecoration: 'none' }}>
            <Typography
              flex={1}
              weight="bold"
              textAlign="center"
              color="primary.A100"
              sx={{
                fontSize: 14,
                lineHeight: '24px'
              }}
            >
              خرید سایر ارزهای دیجیتال
            </Typography>
          </a>
        </Stack>
      )}
    </Paper>
  );
};

export default SimilarCurrencies;
